import { Rng } from '../../core/rng';
import type { ZoneLayout } from '../../zones/types';
import { ZoneBuilder } from '../../zones/ZoneBuilder';

/**
 * EMBERWILD VALE (levels 1–12) — the starting meadowland south of
 * Havenreach. A road runs from the Emberwild Outpost in the south to the city
 * gate in the north; a river cuts across the middle with a stone ford.
 */
export function buildEmberwild(): ZoneLayout {
  const rng = new Rng('emberwild');
  const b = new ZoneBuilder(
    {
      id: 'emberwild', name: 'Emberwild Vale', subtitle: 'Where the first shards fell', kind: 'field', biome: 'meadow',
      levelRange: [1, 12], music: 'meadow', width: 220, depth: 220, seed: 1101,
      ground: ['grass', 'dirt', 'rock', 'dryGrass'], groundScale: [4, 5, 7, 5],
      hills: 3.2, hillScale: 0.016, slopeLayer: 2, patchLayer: 3, patchAmount: 0.12, waterLevel: -4, ambient: 'pollen', mapTint: 0x5a6a3a,
    },
    rng,
  );

  // Main road: outpost (south) → ford → city gate (north), with branches.
  const main = [{ x: 0, z: 104 }, { x: -6, z: 80 }, { x: -14, z: 58 }, { x: -4, z: 34 }, { x: 8, z: 14 }, { x: 4, z: -4 }, { x: -6, z: -30 }, { x: 2, z: -60 }, { x: 0, z: -84 }, { x: 0, z: -110 }];
  b.road(main, 5, 1);
  b.road([{ x: 8, z: 14 }, { x: 40, z: 6 }, { x: 70, z: -18 }, { x: 110, z: -30 }], 4, 1); // east to Sunscar
  b.road([{ x: -6, z: -30 }, { x: -40, z: -44 }, { x: -76, z: -62 }], 3.5, 1); // west to the crypt
  b.river([{ x: -115, z: -6 }, { x: -70, z: 4 }, { x: -30, z: -6 }, { x: 4, z: -8 }, { x: 40, z: -24 }, { x: 80, z: -30 }, { x: 115, z: -44 }], 7, 1.8);
  // Stone ford where the road crosses the river, and a second one east.
  b.flat({ x: 4, z: -6 }, 5, { height: -3.6, layer: 1, soft: 3 });
  b.flat({ x: 50, z: -24 }, 4, { height: -3.6, layer: 1, soft: 3 });

  // Outpost camp (start).
  b.flat({ x: -4, z: 84 }, 13, { layer: 1 });
  b.spawn('start', -2, 90, Math.PI);
  b.spawn('fromTown', 0, -100, 0);
  b.spawn('fromSunscar', 104, -30, -Math.PI / 2);
  b.spawn('fromCrypt', -70, -56, Math.PI * 0.75);
  b.npc('captain_brenna', -8, 80, Math.PI * 0.8);
  b.npc('quartermaster_odo', 5, 86, -Math.PI * 0.6);
  b.npc('scout_lyra', -14, 88, Math.PI * 0.4);
  b.interact({ id: 'ew_waystone_outpost', kind: 'waystone', x: 4, z: 74, data: { name: 'Emberwild Outpost' } });
  b.prop('tent', -14, 78, { rot: 0.6, collider: { w: 3, d: 3 } });
  b.prop('tent', 10, 78, { rot: -0.4, collider: { w: 3, d: 3 } });
  b.prop('campfire', -3, 82, { collider: { r: 0.6 } });
  b.prop('crate', 8, 90, { rot: 0.2, collider: { w: 0.9, d: 0.9 } });
  b.prop('barrel', 9.5, 91, { collider: { r: 0.45 } });
  b.prop('crate', 7.5, 91.5, { rot: 1.1, collider: { w: 0.9, d: 0.9 }, scale: 0.8 });
  b.prop('banner_pole', -9, 72, { collider: { r: 0.3 } });
  b.prop('lamp_post', -9, 94, { collider: { r: 0.25 } });
  b.prop('lamp_post', 6, 70, { collider: { r: 0.25 } });

  // North gate to Havenreach.
  b.flat({ x: 0, z: -96 }, 9, { layer: 1 });
  b.portal({ id: 'ew_to_town', to: 'havenreach', toSpawn: 'fromEmberwild', x: 0, z: -106, radius: 3.5, label: 'Havenreach', kind: 'exit' });
  b.prop('lamp_post', -5, -96, { collider: { r: 0.25 } });
  b.prop('lamp_post', 5, -96, { collider: { r: 0.25 } });
  b.prop('signpost', 7, -88, { collider: { r: 0.3 } });

  // East exit to Sunscar, west trail to the Hollowroot Crypt.
  b.portal({ id: 'ew_to_sunscar', to: 'sunscar', toSpawn: 'fromEmberwild', x: 106, z: -30, radius: 3.5, label: 'Sunscar Expanse', kind: 'exit', minLevel: 11 });
  b.flat({ x: -78, z: -64 }, 10, { layer: 2 });
  b.portal({ id: 'ew_to_crypt', to: 'crypt', toSpawn: 'start', x: -84, z: -70, radius: 3, label: 'Hollowroot Crypt', kind: 'dungeon', minLevel: 7 });
  b.prop('pillar_ruin', -90, -62, { collider: { r: 0.8 }, scale: 1.3 });
  b.prop('pillar_ruin', -78, -76, { collider: { r: 0.8 }, scale: 1.2, rot: 1 });
  b.prop('arch', -84, -72, { collider: { w: 6, d: 1.2 }, rot: 0.8 });

  // Shrine ruins with a vista.
  b.flat({ x: -34, z: -64 }, 11, { layer: 2 });
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    b.prop(i % 3 === 0 ? 'pillar_intact' : 'pillar_ruin', -34 + Math.sin(a) * 8, -64 + Math.cos(a) * 8, { collider: { r: 0.8 }, rot: a, scale: 1.1 });
  }
  b.prop('altar', -34, -64, { collider: { w: 2, d: 1.2 } });
  b.interact({ id: 'ew_vista_shrine', kind: 'vista', x: -34, z: -58, data: { name: 'Shrine of the First Shard' } });
  b.interact({ id: 'ew_lore_shrine', kind: 'lore', x: -36, z: -66, data: { lore: 'shrine_tablet' } });

  // Monster camps.
  b.flat({ x: -56, z: 44 }, 12);
  b.pack({ id: 'ew_wolves_a', monsters: [{ def: 'vale_wolf', count: 3 }], x: -56, z: 44, radius: 6, level: 2, respawn: 45 });
  b.pack({ id: 'ew_wolves_b', monsters: [{ def: 'vale_wolf', count: 3 }], x: -70, z: 22, radius: 6, level: 3, respawn: 45 });
  b.pack({ id: 'ew_wolves_c', monsters: [{ def: 'vale_wolf', count: 4 }], x: -40, z: 66, radius: 6, level: 2, respawn: 45 });
  b.flat({ x: 50, z: 52 }, 14);
  b.pack({ id: 'ew_boars_a', monsters: [{ def: 'thornback_boar', count: 2 }], x: 50, z: 52, radius: 7, level: 3, respawn: 45 });
  b.pack({ id: 'ew_boars_b', monsters: [{ def: 'thornback_boar', count: 3 }], x: 70, z: 30, radius: 7, level: 4, respawn: 45 });
  b.pack({ id: 'ew_boars_c', monsters: [{ def: 'thornback_boar', count: 2 }], x: 32, z: 70, radius: 6, level: 3, respawn: 45 });

  // Grubling warren west of the river.
  b.flat({ x: -60, z: -24 }, 14, { layer: 1 });
  b.pack({ id: 'ew_grubs_a', monsters: [{ def: 'grubling', count: 6 }], x: -60, z: -24, radius: 7, level: 5, respawn: 40 });
  b.pack({ id: 'ew_grubs_b', monsters: [{ def: 'grubling', count: 5 }, { def: 'grubling_brute', count: 1 }], x: -76, z: -36, radius: 7, level: 6, respawn: 60 });
  b.pack({ id: 'ew_grubs_c', monsters: [{ def: 'grubling', count: 5 }], x: -46, z: -40, radius: 6, level: 5, respawn: 40 });
  b.prop('tent', -64, -18, { rot: 2.2, collider: { w: 3, d: 3 }, tint: 0x6a5a3a });
  b.prop('campfire', -60, -26, { collider: { r: 0.6 } });
  b.prop('bones', -56, -22);
  b.prop('bones', -70, -30);

  // Bandit hideout north-east.
  b.flat({ x: 62, z: -66 }, 16, { layer: 1 });
  b.pack({ id: 'ew_bandits_a', monsters: [{ def: 'vale_bandit', count: 3 }, { def: 'bandit_archer', count: 2 }], x: 62, z: -66, radius: 8, level: 8, respawn: 60 });
  b.pack({ id: 'ew_bandits_b', monsters: [{ def: 'vale_bandit', count: 3 }, { def: 'bandit_archer', count: 1 }], x: 80, z: -80, radius: 7, level: 9, respawn: 60 });
  b.pack({ id: 'ew_bandits_c', monsters: [{ def: 'vale_bandit', count: 2 }, { def: 'bandit_archer', count: 2 }], x: 44, z: -86, radius: 7, level: 9, respawn: 60 });
  b.prop('tent', 66, -74, { rot: -0.4, collider: { w: 3, d: 3 }, tint: 0x5a3a3a });
  b.prop('tent', 56, -58, { rot: 0.9, collider: { w: 3, d: 3 }, tint: 0x5a3a3a });
  b.prop('crate', 60, -70, { collider: { w: 0.9, d: 0.9 } });
  b.prop('crate', 61, -71, { collider: { w: 0.9, d: 0.9 }, rot: 0.5 });
  b.prop('barrel', 58, -69, { collider: { r: 0.45 } });
  b.prop('cart', 70, -60, { rot: 1.2, collider: { w: 2.2, d: 1.4 } });
  b.interact({ id: 'ew_bandit_chest', kind: 'chest', x: 72, z: -82, data: { loot: 'bandit_chest' } });

  // Field boss clearing (Elder Thornback).
  b.flat({ x: 58, z: 6 }, 16, { layer: 3 });
  b.pack({ id: 'ew_fieldboss', monsters: [{ def: 'elder_thornback', count: 1 }], x: 58, z: 6, radius: 2, level: 10, respawn: 600, tags: ['fieldBoss'] });

  // Gathering nodes & collectibles.
  const all = { minX: -95, minZ: -95, maxX: 95, maxZ: 95 };
  b.randomSpots(8, all, 18).forEach((p, i) => b.interact({ id: `ew_herb_${i}`, kind: 'gather', x: p.x, z: p.z, data: { skill: 'foraging', node: 'herb' } }));
  b.randomSpots(6, all, 20).forEach((p, i) => b.interact({ id: `ew_timber_${i}`, kind: 'gather', x: p.x, z: p.z, data: { skill: 'logging', node: 'timber' } }));
  b.randomSpots(5, all, 22).forEach((p, i) => b.interact({ id: `ew_ore_${i}`, kind: 'gather', x: p.x, z: p.z, data: { skill: 'mining', node: 'ore' } }));
  b.randomSpots(3, all, 30).forEach((p, i) => b.interact({ id: `ew_dig_${i}`, kind: 'dig', x: p.x, z: p.z, data: { skill: 'excavating' } }));
  b.interact({ id: 'ew_fish_0', kind: 'fish', x: -30, z: -2, data: { skill: 'fishing' } });
  b.interact({ id: 'ew_fish_1', kind: 'fish', x: 72, z: -24, data: { skill: 'fishing' } });
  b.randomSpots(10, all, 16).forEach((p, i) => b.interact({ id: `ew_seed_${i}`, kind: 'seed', x: p.x, z: p.z }));
  b.interact({ id: 'ew_heart', kind: 'heart', x: -88, z: 88, data: { name: 'Heart of the Vale' } });

  // Scenery: forest border, groves, rocks, bushes, clutter.
  b.forestBorder([{ model: 'tree_oak', weight: 3, scale: [0.9, 1.5] }, { model: 'tree_pine', weight: 2, scale: [0.9, 1.4] }], 16, 4.2, [{ x: 0, z: -110 }, { x: 110, z: -30 }, { x: 0, z: 108 }]);
  const inner = { minX: -94, minZ: -94, maxX: 94, maxZ: 94 };
  b.scatterProps([{ model: 'tree_oak', weight: 3, collider: { r: 0.7 }, scale: [0.8, 1.4] }, { model: 'tree_pine', weight: 1, collider: { r: 0.6 }, scale: [0.8, 1.3] }, { model: 'tree_dead', weight: 0.3, collider: { r: 0.5 } }], 90, inner, 6, undefined, 3);
  b.scatterProps([{ model: 'rock_a', weight: 2, collider: { r: 0.9 }, scale: [0.7, 1.6] }, { model: 'rock_b', weight: 2, collider: { r: 0.9 }, scale: [0.6, 1.4] }, { model: 'rock_c', weight: 1, collider: { r: 1.2 }, scale: [0.8, 1.5] }], 45, inner, 7);
  b.scatterProps([{ model: 'bush', weight: 3, scale: [0.8, 1.3], noShadow: true }, { model: 'bush_flower', weight: 2, scale: [0.7, 1.2], noShadow: true }, { model: 'mushroom_cluster', weight: 0.6, noShadow: true }, { model: 'stump', weight: 0.6, collider: { r: 0.5 } }, { model: 'log', weight: 0.6, collider: { w: 2.5, d: 0.7 } }], 140, inner, 3.5);
  b.scatterProps([{ model: 'grass_tuft', weight: 5, scale: [0.8, 1.4], noShadow: true }, { model: 'reeds', weight: 1, noShadow: true }], 320, inner, 2.2, undefined, 0);

  return b.build();
}
